/// <reference types="vite/client" />

import { Alert } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { useDevMode } from "@citrine/client";
import { queryClient } from "@citrine/client/utils";
import { isProduction, isTest } from "@citrine/configuration";

import * as sx from "./version-info.sx";

if (process.env.NODE_ENV === "production") {
  /* eslint-disable no-console */
  console.info(`Citrine Deployment: ${window.AppInfo.deployment}`);
  console.info(`Triton Version: ${window.AppInfo.version}`);
  /* eslint-enable no-console */
}

const VERSION_COOKIE = "Triton_Version";
const VERSION_RE = new RegExp(`(?:^|;) *${VERSION_COOKIE}=([^;]*)`);

function VersionInfoView({ buildVersion }: { buildVersion: string }) {
  const [ver] = useState(() => VERSION_RE.exec(window.document.cookie)?.[1]);

  const [isDevMode] = useDevMode();

  // version cookie only has effect in production
  return isProduction && ver ? (
    <Alert
      variant="filled"
      severity={
        buildVersion && new RegExp(`^${ver}(\\.|$)`).test(buildVersion)
          ? "info"
          : "error"
      }
      sx={{ flexShrink: 0, mx: -2 }}
      onClose={() => {
        // clear the cookie
        window.document.cookie = `${VERSION_COOKIE}=;Path=/;Max-Age=0;`;
        window.location.reload();
      }}
    >
      {/**
       * Showing the cookie value here is not ideal because there could be
       *  various tags that point to the same build
       * It also does not provide any indication of an invalid value
       * Instead, show the actual build version, regardless of what cookie was
       *  used to fetch it
       */}
      {buildVersion}
    </Alert>
  ) : isDevMode ? (
    <sx.Version>{buildVersion}</sx.Version>
  ) : null;
}

const BUILD_TAG_CACHE_KEY = ["build-tag"];

export function VersionInfo() {
  const versionQuery = useQuery({
    queryKey: BUILD_TAG_CACHE_KEY,
    queryFn:
      process.env.NODE_ENV === "production"
        ? () => window.AppInfo.version
        : isTest
          ? () => "test"
          : () =>
              window
                .fetch("/api/v1/proxy/version")
                .then((res) => res.json())
                .then((data: { branch: string }) => data.branch),
  });

  return versionQuery.isSuccess ? (
    <VersionInfoView buildVersion={versionQuery.data} />
  ) : null;
}

if (import.meta.hot) {
  import.meta.hot.on("vite:afterUpdate", () => {
    queryClient.invalidateQueries({ queryKey: BUILD_TAG_CACHE_KEY });
  });
}
